// @flow

import SearchAPIActionCreators from 'search/actions/SearchAPIActionCreators';
import SearchResultsStore from 'search/stores/ResultsStore';
import SearchParamsStore from 'search/stores/ParamsStore';
import * as SearchParamsParser from 'utils/searchParamsParser';
import * as HistoryAPI from 'search/api/historyAPI';
import { apiClient } from 'common/api/apiClient';
import { ApiRequestFailure } from 'common/api/error/apiRequestFailure';
import { searchApiFailureMessage } from 'common/api/error/searchApiFailureMessage';
import { venuesPath } from 'routes';

export default {
  /**
   * Initialize search application stores so that everything will start working
   * once the first React view is created.
   */
  initialize() {
    HistoryAPI.replaceState({
      results: SearchResultsStore.get(),
      params: SearchParamsStore.get()
    });

    HistoryAPI.onPopState(newState => {
      SearchAPIActionCreators.setInitialResults(newState.results);
      SearchAPIActionCreators.setInitialParams(
        SearchParamsParser.parseSearchParams(newState.params)
      );
    });
  },

  /**
   * Initiate a search for venues from the venuu.fi internal API
   */
  searchVenues(): Promise<void> {
    const params = SearchParamsParser.buildSearchParams(
      SearchParamsStore.get()
    );
    const timestamp = Date.now();

    SearchAPIActionCreators.fetchResultsStart(params, timestamp);

    // Search results go stale as venues are edited, published, or
    // unpublished, and nothing in the response tells the browser that.
    // `no-store` keeps a re-run search (including one triggered by back /
    // forward navigation) from being served a cached result set.
    return apiClient({ url: venuesPath(), params, cache: 'no-store' })
      .then(data => {
        SearchAPIActionCreators.fetchResultsSuccess(data, timestamp);
        try {
          HistoryAPI.pushState({
            results: data,
            params: params
          });
        } catch {
          // `pushState` serializes the whole result set into history state, so
          // it throws once that exceeds the browser's per-entry budget, or
          // when Safari rate-limits rapid history writes. Results are already
          // committed to the store and only back/forward fidelity is lost, so
          // swallow it rather than letting it reach the `.catch` below, which
          // would dispatch a failure for a request that succeeded and clear a
          // newer in-flight request's pending timestamp (VEN-1694).
          //
          // Deliberately unreported: the throw has never been observed, and
          // what makes it reachable at all is storing the whole result set in
          // history state. Revisit that when search moves off Flux
          // (VEN-1602). The catch is still needed regardless -- the store bug
          // it works around is VEN-1848.
        }
      })
      .catch(error => {
        // A throw from `fetchResultsSuccess` above lands here too: that
        // dispatch never cleared this request's pending timestamp, so routing
        // to `fetchResultsFailure` clears it instead of leaving an unhandled
        // rejection and a stuck pending entry.
        const message =
          error instanceof ApiRequestFailure
            ? searchApiFailureMessage(error)
            : error.message;
        SearchAPIActionCreators.fetchResultsFailure(
          `Searching for venues failed: ${message}`,
          timestamp
        );
      });
  }
};
